import { ModalLauncher } from '@accedo/modal';
import { logger } from '@accedo/logger';
import { deviceApi } from '@accedo/ctv-device-api';
import { noop } from '@accedo/general-utils';
import { MarqueeData } from '##/components/HomeMarquee/types';
import { nav } from '##/init';
import { setLoadingIndicatorVisibility } from '##/components/LoadingIndicator/LoadingIndicator';
import { VideoBroadcastElement } from '##/services/store/store';
import { HomePageResponse } from '##/services/tenApi/pages/types';
import {
  LiveChannel,
  LiveShow,
  LiveChannelMap,
  LiveV2Response,
} from '##/services/tenApi/collections/types';
import TealiumBridge from './services/analytics/Tealium/TealiumBridge';
import {
  BaseDaiAd,
  ClientSideAd,
  Companion,
} from './services/tenApi/dai/types';
import { PageNavigation } from './utils/PageNavigation';

const versionNumRegex = /HbbTV\/(\d)\.(\d)\.(\d)/g;
const yearRegex = /\b20\d{2}\b/g;
const oldAppUrl = 'http://tv.tenplay.com.au/index.cehtml';
const DEFAULT_FREEVIEW_URL = 'dvb://current.ait/141.4000';

export const VIDEO_PLAYER_NAV_ID = 'videoPlayer';
export const VIDEO_CONTROLS_NAV_ID = 'controls-overlay-navigation';
export const AD_CONTROLS_NAV_ID = 'ad-controls-overlay-navigation';
export const INNOVID_FRAME_ID = 'iroll-frame';
export const INNOVID_CONTAINER_ID = 'iroll-frame-container';
export const MARQUEE_BUTTON_NAV_ID = 'marqueeWatchButton';
export const GENRE_SELECTOR_NAV_ID = 'live-genre-selector';
export const SIDE_MENU_NAV_ID = 'side-menu-nav-id';
export const LIVE_ROW_ID_PREFIX = 'livepage-row';
export const EXIT_MODAL_NAV_ID = 'exit-modal-id';
export const SIGN_OUT_MODAL_NAV_ID = 'sign-out-modal-id';
export const SEARCH_LANDING_BAR_ID = 'searchLandingBar';
export const CONTINUE_WATCHING_ID = 'continue-watching-rail';
export const SEARCH_BUTTON_NAV_ID = 'search-button';

let freeviewUrl: string | undefined = '';

type LoggerMethod = 'trace' | 'debug' | 'info' | 'warn' | 'error' | 'fatal' | 'log';

const SCREEN_LOGGER_TITLE =
  'SCREEN LOGGER: Press <"1" to hide/show> <"5" to clear logs>';
const SCREEN_LOGGER_ELEMENT_ID = 'hbbtvScreenLogger';
const VIDEO_BROADCAST_STYLE =
  'position: absolute; left: 0; top: 0; width: 100%; height: 100%; z-index: -15; visibility: hidden;';

const getAppVersion = (): string => {
  const appVersionStr = navigator.userAgent.match(versionNumRegex)?.[0];

  return appVersionStr?.split('/')?.[1] || '';
};

export const redirectIfSpecNotSupported = (): void => {
  try {
    const appVersion = Number.parseFloat(getAppVersion());
    // HbbTv/1.4.1 = HbbTV 2.0.1
    if (appVersion < 1.4) {
      window.location.href = oldAppUrl;
    }
  } catch (err) {
    logger.error('[utils.ts] Failed to redirect');
  }
};

const noBreakSpace = '\u00A0';

export const getPipeSeperator = (breaks = 2): string => {
  const spaces = new Array(breaks).fill(noBreakSpace).join('');

  return `${spaces}|${spaces}`;
};

export const defer = async (callback: () => void): Promise<void> =>
  new Promise((resolve) =>
    window.setTimeout(async () => {
      await callback();
      resolve();
    }, 1),
  );

export const isHomeRoute = (url: string): boolean =>
  url === '/' || url === '' || url === '/home';

let assignFocusTimeoutId: NodeJS.Timeout;
export const tryAssignFocus = (id: string, attempt = 1): void => {
  clearTimeout(assignFocusTimeoutId);
  if (attempt > 5) return;

  assignFocusTimeoutId = setTimeout(async () => {
    try {
      if (!ModalLauncher.isModalOpen()) {
        await nav.assignFocus(id);
      }
      clearTimeout(assignFocusTimeoutId);
    } catch (err) {
      tryAssignFocus(id, attempt + 1);
    }
  }, 500);
};

type SetStateCallback<T> = (prevState: T) => T;

type SetState<T> = (stateOrSetter: T | SetStateCallback<T>) => void;

export function getCurrentStateValue<T>(setState: SetState<T>): Promise<T> {
  return new Promise((resolve) => {
    setState((prevState: T) => {
      resolve(prevState);

      return prevState;
    });
  });
}

/*
HbbTV 1.5	TS 102 796 v1.2.1 – Nov. 2012
HbbTV 2.0	TS 102 796 v1.3.1 – Nov. 2015
HbbTV 2.0.1	TS 102 796 V1.4.1 – Apr. 2016
HbbTV 2.0.2	TS 102 796 V1.5.1 – Sep. 2018
HbbTV 2.0.3	TS 102 796 V1.6.1 – Oct. 2020
*/

const versionMap = {
  '1.2.1': '1.5',
  '1.3.1': '2.0',
  '1.4.1': '2.0.1',
  '1.5.1': '2.0.2',
  '1.6.1': '2.0.3',
};

type VersionKey = keyof typeof versionMap;

export const getHbbTVSpecVersion = (): string =>
  versionMap[getAppVersion() as VersionKey];

export const getHbbTvPlatformVersion = (): string =>
  `HbbTV ${getHbbTVSpecVersion() || ''}`;

export const MAX_MARQUEE_CHAR_LENGTH = 24;

export const processMarqueeTitle = (title = ''): string => {
  if (title.length > MAX_MARQUEE_CHAR_LENGTH) {
    const titleContents = title.split(' ').filter(Boolean);

    if (titleContents.length < 2) {
      return title;
    }

    const lastItem = titleContents.pop();

    return `${titleContents.join(' ')}\n${lastItem}`;
  }

  return title;
};

export const reformatSubtitleStr = (subtitle = ''): string => {
  if (subtitle.includes('|')) {
    const parts = subtitle.split('|');

    if (parts?.length < 2) {
      return subtitle;
    }

    return parts.map((part) => part?.trim() || part).join(getPipeSeperator());
  }

  return subtitle;
};

export const isBoolean = (value: unknown): boolean =>
  typeof value === 'boolean';

export const isFalse = (value: unknown): boolean => isBoolean(value) && !value;

export const setFreeviewUrl = (url: string | undefined): void => {
  freeviewUrl = url;
};

export const getFreeviewUrl = (): string => freeviewUrl || DEFAULT_FREEVIEW_URL;

const getAlternateVideoBroadcastObject = (): VideoBroadcastElement => {
  /* used only if window.oipfObjectFactory.createVideoBroadcastObject() is not found */
  const broadcastElm = document.createElement('object');
  broadcastElm.type = 'video/broadcast';
  broadcastElm.setAttribute?.('style', VIDEO_BROADCAST_STYLE);
  return broadcastElm as VideoBroadcastElement;
};

const invokeFunctionWithDelay = async (func = noop, delay = 5): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(() => {
      func();
      resolve();
    }, delay * 1000);
  });

const createVideoBroadcastObject = (): void => {
  document.body.appendChild(
    window?.oipfObjectFactory?.createVideoBroadcastObject?.() ||
      getAlternateVideoBroadcastObject(),
  );
};

export const getVideoBroadcastElement = ():
  | VideoBroadcastElement
  | undefined => {
  try {
    return Array.from(document.getElementsByTagName('object')).find(
      (elm) => elm.type === 'video/broadcast',
    ) as VideoBroadcastElement;
  } catch (err) {}

  return undefined;
};

export const isLG = (): boolean => {
  const uaString = window.navigator.userAgent.toLowerCase();

  return (
    (uaString.includes('webos') || uaString.includes('web0s')) &&
    uaString.includes('lge')
  );
};

export const isLg2020TV = (): boolean => {
  const uaString = window.navigator.userAgent.toLowerCase();

  return isLG() && uaString.includes('webos5.0'); // WebOS 5.0 = LG 2020
};

const isHisenseTV = (year: string): boolean => {
  const uaString = window.navigator.userAgent.toLowerCase();

  return uaString.includes('hisense') && uaString.includes(year);
};

export const isHisense2018TV = (): boolean => isHisenseTV('2018');

export const isHisense2019TV = (): boolean => isHisenseTV('2019');

export const isSamsung = (): boolean => {
  const uaString = navigator.userAgent.toLowerCase();

  return uaString.includes('samsung');
};

const maintainBroadcastRelatedState = (): boolean =>
  isHisense2018TV() || isSamsung() || isLG();

const bindToCurrentChannelAndStopStream = (): void => {
  try {
    createVideoBroadcastObject();

    const videoBroadcast = getVideoBroadcastElement();

    /*
        keep application "broadcast related" on all Samsung and Hisense 2018 models
        due to issue in deeplinking to Freeview EPG
      */

    if (
      !videoBroadcast?.setChannel ||
      (maintainBroadcastRelatedState() &&
        videoBroadcast?.stop &&
        videoBroadcast?.bindToCurrentChannel)
    ) {
      videoBroadcast?.bindToCurrentChannel?.();
      videoBroadcast?.stop?.();
    } else {
      // turn application "broadcast independent" to prevent interference with video playback
      videoBroadcast?.setChannel?.(null);
    }

    videoBroadcast?.setAttribute?.('style', VIDEO_BROADCAST_STYLE);
  } catch (err) {
    logger.error(
      '[stopBroadcastStream] error stopping video broadcast stream',
      err,
    );
  }
};

export const resumeBroadcastStreamOnBroadcastRelated = (): void => {
  if (maintainBroadcastRelatedState()) {
    logger.info(
      '[utils.ts] bind videoBroadcast to current channel on app exit',
    );

    getVideoBroadcastElement()?.bindToCurrentChannel?.();
  }
};

export const stopBroadcastStream = async (
  withBindToCurrentChannelCall = true,
): Promise<void> => {
  if (withBindToCurrentChannelCall) {
    await invokeFunctionWithDelay(bindToCurrentChannelAndStopStream);
  }

  invokeFunctionWithDelay(() => {
    if (isLG()) {
      try {
        logger.info('[stopBroadcastStream]: re-invoke stop() on LG devices');

        getVideoBroadcastElement()?.stop?.();
      } catch (err) {}
    }
  }, 1);
};

export const launchFreeviewApp = (): void => {
  try {
    if (maintainBroadcastRelatedState()) {
      getVideoBroadcastElement()?.bindToCurrentChannel?.();
    }

    deviceApi?.platform?.launch?.({ source: getFreeviewUrl() });
  } catch (err) {
    setLoadingIndicatorVisibility?.(false);
    logger.error('[launchFreeviewApp]: Failed to launch freeview app');
  }
};

type ConsoleKey = 'error' | 'debug' | 'info' | 'warn' | 'trace';

const consoleKeys: ConsoleKey[] = ['error', 'debug', 'info', 'warn', 'trace'];

const shouldHideScreenLogger = (): boolean =>
  !window.location.hostname.includes('ten.ocs.ps.accedo.tv');

export const toggleScreenLogger = (): void => {
  if (shouldHideScreenLogger()) {
    return;
  }

  const screenLoggerElement = document.getElementById(SCREEN_LOGGER_ELEMENT_ID);

  if (screenLoggerElement) {
    if (screenLoggerElement.style.display === 'none') {
      screenLoggerElement.style.display = 'block';
    } else {
      screenLoggerElement.style.display = 'none';
    }
  }
};

const processArgs = (key: string, args: any[]): string => {
  let line = `[${key.toUpperCase()}]: `;

  args.forEach((arg) => {
    if (typeof arg === 'object') {
      if (arg instanceof Error) {
        line += arg.toString();
      } else {
        try {
          line += JSON.stringify(arg);
        } catch (err) {
          line += arg.toString();
        }
      }
    } else {
      line += arg;
    }
    line += ' ';
  });

  return line;
};

export const clearScreenLogger = (): void => {
  if (shouldHideScreenLogger()) {
    return;
  }

  try {
    const loggerElm = document.getElementById(SCREEN_LOGGER_ELEMENT_ID);

    if (loggerElm) {
      loggerElm.innerText = SCREEN_LOGGER_TITLE;
    }
  } catch (err) {}
};

export const addScreenLogger = (): void => {
  if (shouldHideScreenLogger()) {
    return;
  }

  const divTag = document.createElement('div');
  divTag.innerText =
    'SCREEN LOGGER: Press <"1" to hide/show> <"5" to clear logs>';
  divTag.setAttribute('id', SCREEN_LOGGER_ELEMENT_ID);
  divTag.setAttribute(
    'style',
    'height: 100vh; width: 60vw; background: rgba(0, 0, 0, 0.7); position: fixed; top: 0; right: 0; padding: 1%; overflow-y: scroll;',
  );
  document.body.appendChild(divTag);

  consoleKeys.forEach((key) => {
    const consoleFunc = console[key] as Function;

    if (consoleFunc) {
      const modifiedConsoleFunc = (...args: any[]): void => {
        const innerText = `${divTag.innerText}\n\n${processArgs(key, args)}`;

        divTag.innerText = innerText.slice(innerText.length - 10000);

        divTag.scrollTop = divTag.scrollHeight;

        consoleFunc?.apply(console, args);
      };

      console[key as ConsoleKey] = modifiedConsoleFunc;
    }
  });
};

export const exitApp = async (): Promise<void> => {
  try {
    await TealiumBridge.onAppExit();
  } catch (err) {}

  try {
    if (isHisense2019TV()) {
      window.close();

      return;
    }

    await deviceApi?.lifecycle?.exit?.();
  } catch (err) {
    window.close();
  }
};

export const isSamsung2021AndAbove = (): boolean => {
  const uaString = navigator.userAgent.toLowerCase();

  if (!uaString.includes('samsung')) {
    return false;
  }

  let modelYear = 0;

  const modelYears = uaString.match(yearRegex) as string[];

  if (modelYears?.length) {
    modelYear = Math.max(...modelYears.map((item) => parseInt(item, 10)));
  }

  return Boolean(modelYear) && modelYear >= 2021;
};

export const safeAssignFocus = (
  targetNodeId: string,
  backupNodeId?: string,
): void => {
  try {
    if (nav.getNode(targetNodeId)) {
      nav.assignFocus(targetNodeId);
    } else if (backupNodeId && nav.getNode(backupNodeId)) {
      nav.assignFocus(backupNodeId);
    } else {
      throw new Error('nodes not found');
    }
  } catch (err) {
    logger.error('[safeAssignFocus] error assigning focus: ', err);
  }
};

export const toLowerWithoutSpaces = (str: string): string => {
  if (typeof str !== 'string') {
    return str;
  }

  return str
    .toLowerCase()
    .trim()
    .split(' ')
    .join('');
};

export const findAndRemoveVideoElement = (): void => {
  try {
    const videoElement = document.body.getElementsByTagName('video')[0];

    videoElement.remove();

    logger.info('[utils] removed player <video> node ');
  } catch (err) {
    logger.info('[utils] Error removing video element: ', err);
  }
};

export const insertMarqueeItems = (
  homePageData: HomePageResponse,
  marqueeData: MarqueeData,
): HomePageResponse => {
  if (
    !homePageData.length ||
    !homePageData?.[0] ||
    !marqueeData ||
    !marqueeData?.items
  ) {
    return homePageData;
  }

  const filteredMarqueeData = {
    ...marqueeData,
    items: marqueeData.items.filter(
      (marqueeItem) => marqueeItem?.ctaType?.toLowerCase() !== 'channel',
    ),
  };

  if (homePageData[0].type?.toLowerCase() === 'marquee') {
    return [filteredMarqueeData, ...homePageData.slice(1)];
  }

  return [filteredMarqueeData, ...homePageData];
};

export const findChannel = (
  channelList: LiveChannel[],
  channelQueryParam: string,
): LiveChannel | undefined => {
  if (!channelList?.length || !channelQueryParam) {
    return undefined;
  }

  const channelParam = channelQueryParam?.toLowerCase();

  return channelList.find(({ key, name }) => {
    const channelId = key?.toLowerCase();

    return (
      name === channelParam ||
      channelId === channelParam ||
      channelId?.includes(channelParam)
    );
  });
};

type OnceCallback<T> = (...args: any[]) => T;
type OnceReturnValue<V> = [() => V, () => boolean];

export const runOnce = <T>(
  fn: OnceCallback<T>,
  args: any[] = [],
): OnceReturnValue<T> => {
  let hasRun = false;
  let result: T;

  return [
    () => {
      if (!hasRun) {
        hasRun = true;

        result = fn(...args);
      }

      return result;
    },
    () => hasRun,
  ];
};

export const isInnovidAd = (daiAd: BaseDaiAd | ClientSideAd): boolean =>
  daiAd?.ad_system?.toLowerCase().includes('innovid');

export const findInnovidCompanionAd = (daiAd: BaseDaiAd): Companion | null => {
  if (daiAd?.companions?.length) {
    const { companions } = daiAd;

    return (
      companions?.find((companion) => companion?.type === 'iframe') ||
      companions[0]
    );
  }

  return null;
};

export const getLogFunctionWithPrefix = (
  prefixStr: string,
  logFunctionName: LoggerMethod,
) => (...args: unknown[]): void => {
  const argArr = [...args];

  if (typeof argArr?.[0] === 'string') {
    argArr[0] = `${prefixStr} ${argArr[0]}`;
  } else {
    argArr?.unshift(prefixStr);
  }

  logger[logFunctionName](...argArr);
};

export const noGenreGroup = {
  id: 'uncategorised',
  name: 'Uncategorised',
  keys: [],
};

const getFilteredSchedule = (channel: LiveChannel): LiveShow[] => {
  const { schedule } = channel;

  const filteredSchedule = schedule?.filter((show) => {
    const endTime = show?.endTime * 1000;

    return Date.now() < endTime;
  });

  const scheduleList = filteredSchedule?.length ? filteredSchedule : schedule;

  return scheduleList?.slice(0, 3) || [];
};

export const getProcessedLiveV2Response = (
  liveV2ResponseFromApi: LiveV2Response,
): LiveV2Response => {
  const { channels, groups } = liveV2ResponseFromApi;

  const channelMap = channels?.reduce<LiveChannelMap>(
    (acc, curr, idx) => ({
      ...acc,
      [curr?.key as string]: { ...curr, channelIdx: idx },
    }),
    {},
  );

  let channelCount = 0;
  const categorizedSet = new Set<string>();

  const channelList: LiveChannel[] = [];

  const groupsWithItems = groups
    ?.filter((group) => Boolean(group?.keys?.length))
    .map((group, groupIdx) => {
      const items = group?.keys
        ?.filter((key) => channelMap?.[key])
        .map((channelKey) => {
          const channelProps = channelMap[channelKey as string];

          const filteredSchedule = getFilteredSchedule(channelProps);

          const channelItem = {
            ...channelProps,
            liveShow: filteredSchedule?.[0],
            schedule: filteredSchedule,
            channelIdx: channelCount,
            genre: group?.name,
            genreIdx: groupIdx,
            channelKey: channelProps.key,
          };

          channelList.push(channelItem);

          categorizedSet.add(channelKey);

          channelCount += 1;

          return channelItem;
        });

      return {
        ...group,
        items,
      };
    });

  if (categorizedSet.size < channels.length && !groups.length) {
    // add frontend uncategorised genre here

    groupsWithItems?.push({
      ...noGenreGroup,
      items: channels.slice(categorizedSet.size).map((uncategorisedChannel) => {
        const filteredSchedule = getFilteredSchedule(uncategorisedChannel);

        const newChannelItem = {
          ...uncategorisedChannel,
          schedule: filteredSchedule,
          liveShow: filteredSchedule?.[0],
          channelIdx: channelCount,
          genreIdx: groups?.length,
          genre: noGenreGroup.id,
          channelKey: uncategorisedChannel.key,
        };

        channelCount += 1;

        channelList.push(newChannelItem);

        return newChannelItem;
      }),
    });

    categorizedSet.clear();
  }

  return {
    ...liveV2ResponseFromApi,
    groups:
      groupsWithItems?.filter((group) => Boolean(group?.items?.length)) || [],
    channels: channelList,
  };
};

export const isHomeLiveCarouselId = (navId: string): boolean =>
  /section-(\d{1}|\d{2})-Live/gi.test(navId);

export const onBackPressOnPagesWithSideNav = (
  currentParentNavId: string,
  secondaryNavId: string,
): boolean => {
  switch (currentParentNavId) {
    case secondaryNavId:
      safeAssignFocus(SIDE_MENU_NAV_ID);

      return true;
    case SIDE_MENU_NAV_ID:
      PageNavigation.showExitPopup();

      return true;
    default:
      return false;
  }
};

export const isShowPage = (route: string): boolean => route?.includes('/show/');

export const getOS = (): string => {
  let agent = navigator.userAgent.toLowerCase();
  let model = 'unknown';

  if (navigator && navigator.userAgent) {
    agent = navigator.userAgent.toLowerCase();

    if (agent.includes('rokubrowser') || agent.includes('roku')) {
      model = 'roku';
    } else if (agent.includes('tizen') || agent.includes('samsung')) {
      model = 'tizen';
    } else if (agent.includes('lge')) {
      model = 'webos';
    } else if (
      agent.includes('hisense') ||
      agent.includes('toshiba') ||
      agent.includes('sharp')
    ) {
      model = 'vidaa';
    } else if (agent.includes('philips')) {
      model = 'philips';
    } else if (agent.includes('tcl')) {
      model = 'tcl';
    } else if (agent.includes('qtembedded')) {
      model = 'hitachi';
    } else if (agent.includes('panasonic')) {
      model = 'panasonic';
    }
  }

  return model;
};
export const BVOD_N10_TOKEN_KEY = 'BVOD_N10_ACCESS_TOKEN';
